import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { signupUser, loginUser, phoneVerify } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Mail, Chrome, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { getFirebaseAuth } from "../lib/firebase";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";

function errText(detail, fallback = "Something went wrong") {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

/**
 * AuthPage is used for both /login and /signup routes.
 * mode="login" → email tab shows email+password; mode="signup" → shows full_name + email + phone + password.
 * Both modes support Google (via Emergent redirect) and Phone OTP (Firebase).
 */
export default function AuthPage({ mode = "login" }) {
  const [tab, setTab] = useState("email");
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/my-orders";

  const finish = (data) => {
    setUser(data);
    toast.success(`Welcome${data.full_name ? `, ${data.full_name}` : ""}`);
    navigate(redirectTo, { replace: true });
  };

  return (
    <main data-testid={`auth-page-${mode}`} className="bg-[#0A0A0A] min-h-screen pt-[100px] pb-24">
      <div className="max-w-md mx-auto px-6">
        <div className="text-[11px] tracking-[0.45em] uppercase text-[#D4AF37] mb-3 text-center">
          ✦ {mode === "signup" ? "Create account" : "Sign in"}
        </div>
        <h1 className="font-serif-display text-5xl text-center tracking-tight" style={{ color: "#FFFFFF" }}>
          {mode === "signup" ? <>Become <em className="not-italic gold-text">Her Box.</em></> : <>Welcome <em className="not-italic gold-text">back.</em></>}
        </h1>

        {/* Tabs */}
        <div className="mt-8 grid grid-cols-2 gap-0 border border-[#262626]">
          <TabBtn active={tab === "email"} onClick={() => setTab("email")} testid="tab-email" icon={<Mail size={14} strokeWidth={1.5} />}>Email</TabBtn>
          <TabBtn active={tab === "google"} onClick={() => setTab("google")} testid="tab-google" icon={<Chrome size={14} strokeWidth={1.5} />}>Google</TabBtn>
        </div>

        <div className="mt-6">
          {tab === "email" && (
            <EmailForm
              mode={mode}
              onAuthed={finish}
              errText={errText}
            />
          )}
          {tab === "google" && <GoogleBlock />}
        </div>

        <p className="mt-8 text-center font-body text-sm" style={{ color: "rgba(255,255,255,0.6)" }}>
          {mode === "signup" ? (<>Already have an account? <Link to="/login" className="gold-text" data-testid="link-to-login">Sign in</Link></>)
            : (<>New here? <Link to="/signup" className="gold-text" data-testid="link-to-signup">Create an account</Link></>)}
        </p>
      </div>
    </main>
  );
}

function TabBtn({ active, onClick, children, testid, icon }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="font-ui transition-all inline-flex items-center justify-center gap-2"
      style={{
        padding: "14px 10px",
        fontSize: 11, fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase",
        borderBottom: active ? "2px solid #F5D565" : "2px solid transparent",
        color: active ? "#F5D565" : "rgba(255,255,255,0.6)",
      }}
    >
      {icon}{children}
    </button>
  );
}

function EmailForm({ mode, onAuthed, errText }) {
  const [form, setForm] = useState({ full_name: "", email: "", phone_number: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const fn = mode === "signup" ? signupUser : loginUser;
      const payload = mode === "signup" ? form : { email: form.email, password: form.password };
      const data = await fn(payload);
      onAuthed(data);
    } catch (ex) {
      setErr(errText(ex?.response?.data?.detail, "Please check your credentials"));
    } finally {
      setBusy(false);
    }
  };

  const h = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form onSubmit={submit} data-testid="email-form" className="space-y-4">
      {mode === "signup" && (
        <>
          <Field label="Full name *" v={form.full_name} on={(v) => h("full_name", v)} testid="email-form-name" required />
          <Field label="Phone number" v={form.phone_number} on={(v) => h("phone_number", v)} testid="email-form-phone" />
        </>
      )}
      <Field label="Email *" type="email" v={form.email} on={(v) => h("email", v)} testid="email-form-email" required />
      <Field label="Password *" type="password" v={form.password} on={(v) => h("password", v)} testid="email-form-password" required minLength={6} />
      {err && <p className="text-[12px] text-red-400" data-testid="email-form-error">{err}</p>}
      <button type="submit" disabled={busy} className="btn-gold w-full disabled:opacity-50" data-testid="email-form-submit">
        {busy ? "Please wait…" : (mode === "signup" ? "Create account" : "Sign in")}
      </button>
    </form>
  );
}

function GoogleBlock() {
  const start = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div data-testid="google-block" className="text-center">
      <p className="font-body text-sm" style={{ color: "rgba(255,255,255,0.7)", marginBottom: 20 }}>
        Sign in with your Google account.
      </p>
      <button onClick={start} data-testid="google-signin-btn"
        className="btn-gold w-full inline-flex items-center justify-center gap-2">
        <Chrome size={16} strokeWidth={1.5} />
        Continue with Google
      </button>
    </div>
  );
}

function PhoneForm({ mode, onAuthed, errText }) {
  const [phone, setPhone] = useState("+91");
  const [code, setCode] = useState("");
  const [fullName, setFullName] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const sendOtp = async (e) => {
    e.preventDefault();
    setErr("");
    if (!/^\+[1-9]\d{7,14}$/.test(phone)) {
      setErr("Enter a valid phone number in E.164 format (e.g. +919876543210)");
      return;
    }
    setBusy(true);
    try {
      const auth = getFirebaseAuth();
      if (!window.__hbRecaptcha) {
        window.__hbRecaptcha = new RecaptchaVerifier(auth, "recaptcha-container", { size: "invisible" });
      }
      const conf = await signInWithPhoneNumber(auth, phone, window.__hbRecaptcha);
      setConfirmation(conf);
      toast.success("OTP sent. Check your SMS.");
    } catch (ex) {
      console.error(ex);
      setErr(ex?.message || "Couldn't send OTP. Check the phone number and try again.");
      try { window.__hbRecaptcha?.clear?.(); } catch {}
      window.__hbRecaptcha = null;
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!confirmation) return;
    setErr("");
    setBusy(true);
    try {
      const result = await confirmation.confirm(code);
      const idToken = await result.user.getIdToken();
      const data = await phoneVerify(idToken, fullName);
      onAuthed(data);
    } catch (ex) {
      setErr(errText(ex?.response?.data?.detail || ex?.message, "Invalid OTP"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="phone-form">
      {!confirmation ? (
        <form onSubmit={sendOtp} className="space-y-4">
          {mode === "signup" && (
            <Field label="Full name" v={fullName} on={setFullName} testid="phone-form-name" />
          )}
          <Field label="Phone (E.164, eg +919876543210) *" v={phone} on={setPhone} testid="phone-form-phone" required />
          <div id="recaptcha-container" />
          {err && <p className="text-[12px] text-red-400" data-testid="phone-form-error">{err}</p>}
          <button type="submit" disabled={busy} className="btn-gold w-full disabled:opacity-50" data-testid="phone-form-send">
            {busy ? <Loader2 size={16} className="animate-spin" /> : "Send OTP"}
          </button>
        </form>
      ) : (
        <form onSubmit={verify} className="space-y-4">
          <p className="font-body text-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
            OTP sent to {phone}
          </p>
          <Field label="6-digit code *" v={code} on={setCode} testid="phone-form-code" required maxLength={6} />
          {err && <p className="text-[12px] text-red-400" data-testid="phone-form-verify-error">{err}</p>}
          <button type="submit" disabled={busy} className="btn-gold w-full disabled:opacity-50" data-testid="phone-form-verify">
            {busy ? "Verifying…" : "Verify & continue"}
          </button>
          <button type="button" onClick={() => { setConfirmation(null); setCode(""); }}
                  className="font-ui text-center w-full mt-2"
                  style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.6)" }}
                  data-testid="phone-form-resend">
            ← Change number
          </button>
        </form>
      )}
    </div>
  );
}

function Field({ label, v, on, testid, type = "text", required = false, minLength, maxLength }) {
  return (
    <label className="block">
      <span className="text-[10px] tracking-[0.3em] uppercase text-[#A3A3A3]">{label}</span>
      <input
        data-testid={testid}
        type={type}
        value={v}
        required={required}
        minLength={minLength}
        maxLength={maxLength}
        onChange={(e) => on(e.target.value)}
        className="mt-2 w-full bg-transparent border-b border-[#262626] py-2.5 text-sm focus:border-[#D4AF37] outline-none"
      />
    </label>
  );
}
